import { Chip } from '@mui/material';
import type { ContractStatus } from '@vy/types';
import { CONTRACT_STATUSES } from '@vy/types';
import { raiseUnreachable } from '@vy/utils';
import cn from 'classnames';
import type { HTMLAttributes } from 'react';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { formatDate } from '../../utils/date';
import { VyTooltip } from '../new-design/VyTooltip';

type ContractStatusChipProps = HTMLAttributes<HTMLSpanElement> & {
  status: ContractStatus;
  placement?: 'top' | 'bottom';
  endDate?: Date;
};

export const ContractStatusChip: React.FC<ContractStatusChipProps> = ({
  status,
  className,
  endDate,
  placement = 'top',
}) => {
  const isTerminationState =
    status === CONTRACT_STATUSES.Terminated || status === CONTRACT_STATUSES.PendingTermination;
  const defaultDate = new Date();

  return (
    <div>
      <VyTooltip
        placement={placement}
        tooltipContent={isTerminationState ? `Termination date: ${formatDate(endDate ?? defaultDate)}` : undefined}
      >
        <Chip
          label={<>{getContractStatusLabel(status)}</>}
          size="medium"
          variant="outlined"
          className={twMerge(
            cn(
              { 'text-active bg-active': status === CONTRACT_STATUSES.Active },
              { 'text-error bg-error': status === CONTRACT_STATUSES.Error },
              { 'text-[#939393] bg-[#ECECEC]': status === CONTRACT_STATUSES.Inactive },
              { 'text-[#D4A342] bg-[#FFF3D6]': isTerminationState },
              { 'text-[#7E4CFF] bg-[#F4F2FF]': status === CONTRACT_STATUSES.InReview },
              { 'text-[#EE2049] bg-[#FEF3F4]': status === CONTRACT_STATUSES.Expired },
            ),
            className,
          )}
        />
      </VyTooltip>
    </div>
  );
};

export function getContractStatusLabel(contractStatus: ContractStatus) {
  switch (contractStatus) {
    case CONTRACT_STATUSES.InReview:
      return 'Pending Review';
    case CONTRACT_STATUSES.Active:
      return 'Active';
    case CONTRACT_STATUSES.Inactive:
      return 'Inactive';
    case CONTRACT_STATUSES.Terminated:
      return 'Termination';
    case CONTRACT_STATUSES.PendingTermination:
      return 'Pending Termination';
    case CONTRACT_STATUSES.Error:
      return 'Error';
    case CONTRACT_STATUSES.Expired:
      return 'Expired';
    default:
      return raiseUnreachable(contractStatus);
  }
}
